import { some, sortBy, uniq, without } from "lodash-es";
import { useCallback, useMemo, useReducer } from "react";
import { i18nService } from "services";
import { IOption } from "types";
import { IInclusionRow } from "./components/TableWrapper";

interface IState {
  search?: string;
  thema?: string | null;
  target_audience?: string | null;
  material?: string | null;
  sortColumn?: string | null;
  sortDirection?: string | null;
  favorites?: string[];
}

const getSortValue = (
  row: IInclusionRow,
  column: string
): string => {
  const value = row[column as keyof IInclusionRow];

  if (Array.isArray(value)) {
    return value.join(", ").toLowerCase();
  }

  if (typeof value === "string") {
    return value.trim().toLowerCase();
  }

  return "";
};

const initialState: IState = {
  search: "",
  thema: null,
  target_audience: null,
  material: null,
  sortColumn: "title",
  sortDirection: "asc",
  favorites: JSON.parse(
    localStorage.getItem("favorite-inclusion-rows") || "[]"
  ),
};

enum Type {
  SET_SEARCH = "SET_SEARCH",
  SET_THEMA = "SET_THEMA",
  SET_TARGET_AUDIENCE = "SET_TARGET_AUDIENCE",
  SET_MATERIAL = "SET_MATERIAL",
  SET_SORT_COLUMN = "SET_SORT_COLUMN",
  SET_SORT_DIRECTION = "SET_SORT_DIRECTION",
  ADD_TO_FAVORITES = "ADD_TO_FAVORITES",
  REMOVE_FROM_FAVORITES = "REMOVE_FROM_FAVORITES",
}

export const useTableSearch = ({ rows }: { rows: IInclusionRow[] }) => {
  const [state, dispatch] = useReducer(
    (state: IState, { type, payload }: { type: Type; payload: any }) => {
      switch (type) {
        case Type.SET_SEARCH: {
          return {
            ...state,
            search: payload.search,
          };
        }

        case Type.SET_THEMA: {
          return {
            ...state,
            thema: payload.thema,
          };
        }

        case Type.SET_TARGET_AUDIENCE: {
          return {
            ...state,
            target_audience: payload.target_audience,
          };
        }

        case Type.SET_MATERIAL: {
          return {
            ...state,
            material: payload.material,
          };
        }

        case Type.SET_SORT_COLUMN: {
          return {
            ...state,
            sortColumn: payload.sortColumn,
          };
        }

        case Type.SET_SORT_DIRECTION: {
          return {
            ...state,
            sortDirection: payload.sortDirection,
          };
        }

        case Type.ADD_TO_FAVORITES: {
          localStorage.setItem(
            "favorite-inclusion-rows",
            JSON.stringify([
              ...JSON.parse(
                localStorage.getItem("favorite-inclusion-rows") || "[]"
              ),
              payload.favorite,
            ])
          );
          return {
            ...state,
            ...(state.favorites && {
              favorites: [...state.favorites, payload.favorite],
            }),
          };
        }

        case Type.REMOVE_FROM_FAVORITES: {
          localStorage.setItem(
            "favorite-inclusion-rows",
            JSON.stringify(
              without(
                JSON.parse(
                  localStorage.getItem("favorite-inclusion-rows") || "[]"
                ),
                payload.favorite
              )
            )
          );
          return {
            ...state,
            ...(state.favorites && {
              favorites: without(state.favorites, payload.favorite),
            }),
          };
        }

        default: {
          return state;
        }
      }
    },
    initialState
  );

  const setSearch = ({ search }: { search: string }) => {
    dispatch({
      type: Type.SET_SEARCH,
      payload: {
        search,
      },
    });
  };

  const setThema = (thema: string | null) => {
    dispatch({
      type: Type.SET_THEMA,
      payload: {
        thema,
      },
    });
  };

  const setTargetAudience = (target_audience: string | null) => {
    dispatch({
      type: Type.SET_TARGET_AUDIENCE,
      payload: {
        target_audience,
      },
    });
  };

  const setMaterial = (material: string | null) => {
    dispatch({
      type: Type.SET_MATERIAL,
      payload: {
        material,
      },
    });
  };

  const themaOptions = useMemo<IOption[]>(
    () => [
      {
        label: i18nService.copy["toolQuickSearch.choose"]?.replace(
          "@filter",
          "thema"
        ),
        value: "",
        type: "",
        name: "",
      },
      ...sortBy(
        uniq(
          rows
            .map(({ theme }) => theme)
            .filter((theme) => !!theme)
            .flat()
        ).map((theme: any) => ({
          label: theme,
          value: theme,
          name: theme,
          type: theme,
        })),
        "label"
      ),
    ],
    [rows]
  );

  const targetAudienceOptions = useMemo<IOption[]>(
    () => [
      {
        label: i18nService.copy["toolQuickSearch.choose"]?.replace(
          "@filter",
          "doelgroep"
        ),
        value: "",
        type: "",
        name: "",
      },
      ...sortBy(
        uniq(
          rows
            .map(({ target_audience }) => target_audience)
            .filter((target_audience) => !!target_audience)
            .flat()
        ).map((target_audience: any) => ({
          label: target_audience,
          value: target_audience,
          name: target_audience,
          type: target_audience,
        })),
        "label"
      ),
    ],
    [rows]
  );

  const materialOptions = useMemo<IOption[]>(
    () => [
      {
        label: i18nService.copy["toolQuickSearch.choose"]?.replace(
          "@filter",
          "materiaal"
        ),
        value: "",
        type: "",
        name: "",
      },
      ...sortBy(
        uniq(
          rows
            .map(({ type }) => type)
            .filter((type) => !!type)
            .flat()
        ).map((type: any) => ({
          label: type,
          value: type,
          name: type,
          type,
        })),
        "label"
      ),
    ],
    [rows]
  );

  const filteredRows = useMemo(() => {
    const filtered = rows.filter(
      ({ title, description, theme, target_audience, type }) => {
        const match = (t: string, target?: string | null) => {
          return !target || t?.toLowerCase()?.includes(target?.toLowerCase());
        };

        const matchesSearch =
          match(title, state.search) ||
          (description && match(description, state.search));

        const matchesTheme = some(theme, (t: string) => match(t, state.thema));

        const matchesTargetAudience = some(target_audience, (t: string) =>
          match(t, state.target_audience)
        );

        const matchesMaterial = some(type, (t: string) =>
          match(t, state.material)
        );

        return (
          matchesSearch &&
          matchesTheme &&
          matchesTargetAudience &&
          matchesMaterial
        );
      }
    );

    const sortColumn = state.sortColumn;

    if (sortColumn === null || sortColumn === undefined)
      return filtered.sort((a, b) => {
        let aIsFavorite = state.favorites?.includes(a.title);
        let bIsFavorite = state.favorites?.includes(b.title);

        // If either A or B is a favorite, prioritize it
        if (aIsFavorite || bIsFavorite) {
          if (aIsFavorite && !bIsFavorite) {
            return -1;
          }
          if (!aIsFavorite && bIsFavorite) {
            return 1;
          }
        }

        // If either A or B is spotlight, prioritize it
        if (a.spotlight || b.spotlight) {
          if (a.spotlight && !b.spotlight) {
            return -1;
          }
          if (!a.spotlight && b.spotlight) {
            return 1;
          }
        }

        return 0;
      });

    return filtered.sort((a, b) => {
      const compareValueA = getSortValue(a, sortColumn);
      const compareValueB = getSortValue(b, sortColumn);

      let aIsFavorite = state.favorites?.includes(a.title);
      let bIsFavorite = state.favorites?.includes(b.title);

      // If either A or B is a favorite, prioritize it
      if (aIsFavorite || bIsFavorite) {
        if (aIsFavorite && !bIsFavorite) {
          return -1;
        }
        if (!aIsFavorite && bIsFavorite) {
          return 1;
        }
      }

      // If either A or B is spotlight, prioritize it
      if (a.spotlight || b.spotlight) {
        if (a.spotlight && !b.spotlight) {
          return -1;
        }
        if (!a.spotlight && b.spotlight) {
          return 1;
        }
      }

      return state.sortDirection === "asc"
        ? compareValueA.localeCompare(compareValueB)
        : compareValueB.localeCompare(compareValueA);
    });
    // eslint-disable-next-line
  }, [
    rows,
    state.material,
    state.search,
    state.sortColumn,
    state.sortDirection,
    state.target_audience,
    state.thema,
  ]);

  const handleSort = useCallback(
    (column: string) => {
      if (state.sortColumn === column) {
        if (state.sortDirection === "desc") {
          dispatch({
            type: Type.SET_SORT_COLUMN,
            payload: {
              sortColumn: null,
            },
          });
        } else {
          dispatch({
            type: Type.SET_SORT_DIRECTION,
            payload: {
              sortDirection: "desc",
            },
          });
        }
      } else {
        dispatch({
          type: Type.SET_SORT_COLUMN,
          payload: {
            sortColumn: column,
          },
        });
        dispatch({
          type: Type.SET_SORT_DIRECTION,
          payload: {
            sortDirection: "asc",
          },
        });
      }
    },
    [state.sortColumn, state.sortDirection]
  );

  const addToFavorites = (favorite: string) => {
    dispatch({
      type: Type.ADD_TO_FAVORITES,
      payload: {
        favorite,
      },
    });
  };

  const removeFromFavorites = (favorite: string) => {
    dispatch({
      type: Type.REMOVE_FROM_FAVORITES,
      payload: {
        favorite,
      },
    });
  };

  return {
    state: {
      sortColumn: state.sortColumn,
      sortDirection: state.sortDirection,
      favorites: state.favorites,
      themaOptions,
      targetAudienceOptions,
      materialOptions,
      filteredRows,
    },
    actions: {
      setSearch,
      setThema,
      setTargetAudience,
      setMaterial,
      handleSort,
      addToFavorites,
      removeFromFavorites,
    },
  };
};
